/**
 * use-time-entries — Phase 3c TanStack Query hooks for the timesheet view.
 *
 *   useTimeEntries(params)        → useInfiniteQuery on
 *                                   ['time-entries', params]; page size 50.
 *                                   Cursor-paginated by the BE.
 *
 *   useTimesheet(params)          → useQuery on ['timesheet', params].
 *                                   Returns groups (per day or per ISO
 *                                   week) with totals + entries.
 *
 *   useCreateTimeEntry()          → useMutation; POST /api/time-entries.
 *                                   Phase 3d — used by the Week-view
 *                                   calendar grid's "click empty cell"
 *                                   create flow. Settle-time invalidates
 *                                   both `time-entries` and `timesheet`
 *                                   families so the new block appears in
 *                                   the grid + entries list.
 *
 *   useUpdateTimeEntry()          → useMutation; PATCH /api/time-entries/{id}.
 *                                   Optimistic: replace the row in every
 *                                   matching ['time-entries'] and
 *                                   ['timesheet'] cache, rollback on error.
 *
 *   useDeleteTimeEntry()          → useMutation; DELETE /api/time-entries/{id}.
 *                                   Optimistic: remove from every matching
 *                                   cache, rollback on error.
 *
 * The optimistic mutations walk every cached query whose key starts with
 * the family root (`['time-entries']` / `['timesheet']`) so an edit made
 * on the timesheet view also reflects on a deliverable-scoped list, and
 * vice versa. Settle-time invalidation runs on both families.
 *
 * `TIME_ENTRIES_QUERY_KEY` from FE-1's `use-running-timer.ts` is the
 * shared family root for the entries list — `useStopTimer` already
 * invalidates it on settle, so a stopped timer drops the resulting
 * historical row into this view automatically.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { InfiniteData, QueryKey } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type {
  DeliverableKind,
  SubmitTimesheetsResponse,
  TimeEntriesListResponse,
  TimeEntry,
  TimesheetDaysResponse,
  TimesheetResponse,
} from '@/api/types-bridge'
import { useToast } from '@/components/ToastProvider'
import { invalidateTimerDerivedCaches } from '@/features/time/use-running-timer'

export interface CreateTimeEntryArgs {
  /** ISO datetime — required. */
  startedAt: string
  /** ISO datetime — required, must be > startedAt. */
  endedAt: string
  /** Both-or-neither with `deliverableId`. */
  deliverableKind?: DeliverableKind
  deliverableId?: string
  projectId?: string | null
  notes?: string
  snapMinutes?: number
}

/* -------------------------------------------------------------------------- */
/*                              Query-key helpers                             */
/* -------------------------------------------------------------------------- */

/**
 * Family root for every entries-list cache. Matches FE-1's
 * `TIME_ENTRIES_QUERY_KEY` so `useStopTimer`'s settle invalidation also
 * sweeps this view.
 */
export const TIME_ENTRIES_FAMILY = ['time-entries'] as const

/** Family root for the grouped timesheet caches. */
export const TIMESHEET_FAMILY = ['timesheet'] as const

export interface TimeEntriesParams {
  /** Inclusive ISO date `YYYY-MM-DD`. */
  from: string
  /** Inclusive ISO date `YYYY-MM-DD`. */
  to: string
  projectId?: string
  deliverableKind?: DeliverableKind
  deliverableId?: string
}

export interface TimesheetParams {
  from: string
  to: string
  groupBy: 'day' | 'week'
}

const PAGE_SIZE = 50

const TIME_ENTRIES_QUERY_KEY = (params: TimeEntriesParams) =>
  [...TIME_ENTRIES_FAMILY, params] as const
const TIMESHEET_QUERY_KEY = (params: TimesheetParams) => [...TIMESHEET_FAMILY, params] as const

type TimeEntriesCache = InfiniteData<TimeEntriesListResponse, string | null>

/* -------------------------------------------------------------------------- */
/*                              URL builders                                  */
/* -------------------------------------------------------------------------- */

function buildEntriesUrl(params: TimeEntriesParams, cursor: string | null): string {
  const search = new URLSearchParams({
    from: params.from,
    to: params.to,
    limit: String(PAGE_SIZE),
  })
  if (params.projectId) search.set('projectId', params.projectId)
  if (params.deliverableKind) search.set('kind', params.deliverableKind)
  if (params.deliverableId) search.set('deliverableId', params.deliverableId)
  if (cursor) search.set('cursor', cursor)
  return `/api/time-entries?${search.toString()}`
}

function buildTimesheetUrl(params: TimesheetParams): string {
  const search = new URLSearchParams({
    from: params.from,
    to: params.to,
    groupBy: params.groupBy,
  })
  return `/api/timesheets?${search.toString()}`
}

/* -------------------------------------------------------------------------- */
/*                              useTimeEntries                                */
/* -------------------------------------------------------------------------- */

export function useTimeEntries(params: TimeEntriesParams, opts: { enabled?: boolean } = {}) {
  return useInfiniteQuery<
    TimeEntriesListResponse,
    Error,
    TimeEntriesCache,
    ReturnType<typeof TIME_ENTRIES_QUERY_KEY>,
    string | null
  >({
    queryKey: TIME_ENTRIES_QUERY_KEY(params),
    queryFn: async ({ pageParam }) => {
      const res = await apiClient<{ data: TimeEntriesListResponse }>(
        buildEntriesUrl(params, pageParam ?? null),
      )
      return res.data
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/* -------------------------------------------------------------------------- */
/*                              useTimesheet                                  */
/* -------------------------------------------------------------------------- */

export function useTimesheet(params: TimesheetParams, opts: { enabled?: boolean } = {}) {
  return useQuery<TimesheetResponse>({
    queryKey: TIMESHEET_QUERY_KEY(params),
    queryFn: async () => {
      const res = await apiClient<{ data: TimesheetResponse }>(buildTimesheetUrl(params))
      return res.data
    },
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/* -------------------------------------------------------------------------- */
/*                              Cache-write helpers                           */
/* -------------------------------------------------------------------------- */

/**
 * Recompute group totals after an entry mutation. The BE re-derives this
 * on settle, but doing it locally keeps the optimistic UI honest.
 */
function durationSeconds(entry: TimeEntry): number {
  if (!entry.endedAt) return 0
  const start = new Date(entry.startedAt).getTime()
  const end = new Date(entry.endedAt).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return 0
  return Math.max(0, Math.floor((end - start) / 1000))
}

/**
 * Apply an entry-level mapper to every cached `['time-entries', ...]`
 * page. Returns the snapshot we mutated so onError can roll it back.
 */
function patchAllEntriesCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  patcher: (entry: TimeEntry) => TimeEntry | null,
): Array<{ key: QueryKey; previous: TimeEntriesCache | undefined }> {
  const queries = queryClient.getQueriesData<TimeEntriesCache>({ queryKey: TIME_ENTRIES_FAMILY })
  const snapshot: Array<{ key: QueryKey; previous: TimeEntriesCache | undefined }> = []
  for (const [key, cache] of queries) {
    snapshot.push({ key, previous: cache })
    if (!cache) continue
    queryClient.setQueryData<TimeEntriesCache>(key, {
      ...cache,
      pages: cache.pages.map((page) => {
        const next: TimeEntry[] = []
        for (const item of page.items) {
          const patched = patcher(item)
          if (patched !== null) next.push(patched)
        }
        return { ...page, items: next }
      }),
    })
  }
  return snapshot
}

function patchAllTimesheetCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  patcher: (entry: TimeEntry) => TimeEntry | null,
): Array<{ key: QueryKey; previous: TimesheetResponse | undefined }> {
  const queries = queryClient.getQueriesData<TimesheetResponse>({ queryKey: TIMESHEET_FAMILY })
  const snapshot: Array<{ key: QueryKey; previous: TimesheetResponse | undefined }> = []
  for (const [key, cache] of queries) {
    snapshot.push({ key, previous: cache })
    if (!cache) continue
    queryClient.setQueryData<TimesheetResponse>(key, {
      groups: cache.groups.map((group) => {
        const nextEntries: TimeEntry[] = []
        for (const item of group.entries) {
          const patched = patcher(item)
          if (patched !== null) nextEntries.push(patched)
        }
        const totalSeconds = nextEntries.reduce((acc, e) => acc + durationSeconds(e), 0)
        return { ...group, entries: nextEntries, totalSeconds }
      }),
    })
  }
  return snapshot
}

function rollbackEntries(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: Array<{ key: QueryKey; previous: TimeEntriesCache | undefined }>,
) {
  for (const { key, previous } of snapshot) {
    queryClient.setQueryData(key, previous)
  }
}

function rollbackTimesheet(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshot: Array<{ key: QueryKey; previous: TimesheetResponse | undefined }>,
) {
  for (const { key, previous } of snapshot) {
    queryClient.setQueryData(key, previous)
  }
}

function invalidateBothFamilies(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: TIME_ENTRIES_FAMILY })
  queryClient.invalidateQueries({ queryKey: TIMESHEET_FAMILY })
  // The per-day status/total rows feed the week-header badges and the
  // Submit modal; a manual create / edit / delete changes them too, and
  // without this the header showed a stale badge until reload.
  queryClient.invalidateQueries({ queryKey: TIMESHEET_DAYS_FAMILY })
}

/* -------------------------------------------------------------------------- */
/*                              useCreateTimeEntry                            */
/* -------------------------------------------------------------------------- */

/**
 * Create a closed historical entry. Used by the Week-view calendar grid
 * (Phase 3d) when the user clicks an empty cell to log a past block of
 * time, distinct from the live `useStartTimer` flow.
 *
 * No optimistic insert — the closed-entry shape needs a server-stamped
 * id + tenantId/userId, and the calendar grid renders the new block on
 * the next settle. Toasts on error so the modal can stay open and let the
 * user adjust + retry.
 */
export function useCreateTimeEntry() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation<TimeEntry, Error, CreateTimeEntryArgs>({
    mutationFn: async (input) => {
      const res = await apiClient<{ data: TimeEntry }>('/api/time-entries', {
        method: 'POST',
        body: input,
      })
      return res.data
    },
    onError: () => {
      toast.error('Failed to create time entry. Please try again.')
    },
    onSettled: () => {
      invalidateBothFamilies(queryClient)
    },
  })
}

/* -------------------------------------------------------------------------- */
/*                              useUpdateTimeEntry                            */
/* -------------------------------------------------------------------------- */

export interface UpdateTimeEntryArgs {
  id: string
  patch: {
    startedAt?: string
    endedAt?: string | null
    notes?: string
    snapMinutes?: number
    /**
     * Re-link the entry to a different deliverable. Must be set together
     * (or both `null` to detach). The BE re-resolves projectId from the
     * new deliverable. Phase 3d parity with the legacy edit modal.
     */
    deliverableKind?: import('@/api/types-bridge').DeliverableKind | null
    deliverableId?: string | null
    projectId?: string | null
  }
}

interface UpdateTimeEntryContext {
  entriesSnapshot: Array<{ key: QueryKey; previous: TimeEntriesCache | undefined }>
  timesheetSnapshot: Array<{ key: QueryKey; previous: TimesheetResponse | undefined }>
}

export function useUpdateTimeEntry() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation<TimeEntry, Error, UpdateTimeEntryArgs, UpdateTimeEntryContext>({
    mutationFn: async ({ id, patch }) => {
      const res = await apiClient<{ data: TimeEntry }>(`/api/time-entries/${id}`, {
        method: 'PATCH',
        body: patch,
      })
      return res.data
    },
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: TIME_ENTRIES_FAMILY })
      await queryClient.cancelQueries({ queryKey: TIMESHEET_FAMILY })

      const now = new Date().toISOString()
      const apply = (entry: TimeEntry): TimeEntry | null => {
        if (entry.id !== id) return entry
        const merged: TimeEntry = { ...entry, updatedAt: now }
        if (patch.startedAt !== undefined) merged.startedAt = patch.startedAt
        if (patch.endedAt !== undefined) merged.endedAt = patch.endedAt
        if (patch.notes !== undefined) merged.notes = patch.notes
        if (patch.snapMinutes !== undefined) merged.snapMinutes = patch.snapMinutes
        if (patch.deliverableKind !== undefined) merged.deliverableKind = patch.deliverableKind
        if (patch.deliverableId !== undefined) merged.deliverableId = patch.deliverableId
        if (patch.projectId !== undefined) merged.projectId = patch.projectId
        return merged
      }

      const entriesSnapshot = patchAllEntriesCaches(queryClient, apply)
      const timesheetSnapshot = patchAllTimesheetCaches(queryClient, apply)
      return { entriesSnapshot, timesheetSnapshot }
    },
    onError: (_err, _args, context) => {
      if (context) {
        rollbackEntries(queryClient, context.entriesSnapshot)
        rollbackTimesheet(queryClient, context.timesheetSnapshot)
      }
      toast.error('Failed to update time entry. Please try again.')
    },
    onSettled: () => {
      invalidateBothFamilies(queryClient)
    },
  })
}

/* -------------------------------------------------------------------------- */
/*                              useDeleteTimeEntry                            */
/* -------------------------------------------------------------------------- */

export interface DeleteTimeEntryArgs {
  id: string
}

interface DeleteTimeEntryContext {
  entriesSnapshot: Array<{ key: QueryKey; previous: TimeEntriesCache | undefined }>
  timesheetSnapshot: Array<{ key: QueryKey; previous: TimesheetResponse | undefined }>
}

export function useDeleteTimeEntry() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteTimeEntryArgs, DeleteTimeEntryContext>({
    mutationFn: async ({ id }) => {
      await apiClient<void>(`/api/time-entries/${id}`, { method: 'DELETE' })
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: TIME_ENTRIES_FAMILY })
      await queryClient.cancelQueries({ queryKey: TIMESHEET_FAMILY })

      const drop = (entry: TimeEntry): TimeEntry | null => (entry.id === id ? null : entry)
      const entriesSnapshot = patchAllEntriesCaches(queryClient, drop)
      const timesheetSnapshot = patchAllTimesheetCaches(queryClient, drop)
      return { entriesSnapshot, timesheetSnapshot }
    },
    onError: (_err, _args, context) => {
      if (context) {
        rollbackEntries(queryClient, context.entriesSnapshot)
        rollbackTimesheet(queryClient, context.timesheetSnapshot)
      }
      // No toast here: the caller (`DeleteTimeEntryModal`) owns the
      // localized error copy, and a second toast from the hook doubled it.
    },
    onSettled: () => {
      invalidateBothFamilies(queryClient)
      // A deleted entry may have been the running one; the header timer,
      // the card ▶ buttons and the Available Work rail all key off it.
      invalidateTimerDerivedCaches(queryClient)
    },
  })
}

/* -------------------------------------------------------------------------- */
/*                              useApproveTimeEntry                           */
/* -------------------------------------------------------------------------- */

export interface ApproveTimeEntryArgs {
  id: string
}

interface ApproveTimeEntryContext {
  entriesSnapshot: Array<{ key: QueryKey; previous: TimeEntriesCache | undefined }>
  timesheetSnapshot: Array<{ key: QueryKey; previous: TimesheetResponse | undefined }>
}

/**
 * Flip a `pending` time entry to `approved`. Wired to the green check on
 * each calendar entry block. Manual entries are already `approved` —
 * clicking the check on those is a no-op (BE returns the unchanged row).
 *
 * Optimistic update applies the status flip across every entries / timesheet
 * cache so the UI repaints immediately; rolls back on failure.
 */
export function useApproveTimeEntry() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation<TimeEntry, Error, ApproveTimeEntryArgs, ApproveTimeEntryContext>({
    mutationFn: async ({ id }) => {
      const res = await apiClient<{ data: TimeEntry }>(`/api/time-entries/${id}/approve`, {
        method: 'PATCH',
      })
      return res.data
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: TIME_ENTRIES_FAMILY })
      await queryClient.cancelQueries({ queryKey: TIMESHEET_FAMILY })

      const apply = (entry: TimeEntry): TimeEntry =>
        entry.id === id ? { ...entry, status: 'approved' } : entry
      const entriesSnapshot = patchAllEntriesCaches(queryClient, apply)
      const timesheetSnapshot = patchAllTimesheetCaches(queryClient, apply)
      return { entriesSnapshot, timesheetSnapshot }
    },
    onError: (_err, _args, context) => {
      if (context) {
        rollbackEntries(queryClient, context.entriesSnapshot)
        rollbackTimesheet(queryClient, context.timesheetSnapshot)
      }
      toast.error('Failed to approve time entry. Please try again.')
    },
    onSettled: () => {
      invalidateBothFamilies(queryClient)
    },
  })
}

/* -------------------------------------------------------------------------- */
/*                              useTimesheetDays                              */
/* -------------------------------------------------------------------------- */

export const TIMESHEET_DAYS_FAMILY = ['timesheet-days'] as const

export interface TimesheetDaysParams {
  start: string
  end: string
}

const TIMESHEET_DAYS_QUERY_KEY = (params: TimesheetDaysParams) =>
  [...TIMESHEET_DAYS_FAMILY, params] as const

/**
 * Fetch per-day timesheet rows for a 7-day window. Backs the Submit
 * Timesheets modal. Returns one row per day in the inclusive [start, end]
 * range; missing days are padded by the BE with `status: 'pending'` and
 * `totalMinutes: 0`.
 */
export function useTimesheetDays(params: TimesheetDaysParams, opts: { enabled?: boolean } = {}) {
  return useQuery<TimesheetDaysResponse>({
    queryKey: TIMESHEET_DAYS_QUERY_KEY(params),
    queryFn: async () => {
      const qs = new URLSearchParams({ start: params.start, end: params.end })
      const res = await apiClient<{ data: TimesheetDaysResponse }>(
        `/api/timesheets/days?${qs.toString()}`,
      )
      return res.data
    },
    enabled: opts.enabled ?? true,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/* -------------------------------------------------------------------------- */
/*                              useSubmitTimesheets                           */
/* -------------------------------------------------------------------------- */

export interface SubmitTimesheetsArgs {
  /** Inclusive upper bound — every unsubmitted day up to this date is submitted. */
  maxDate: string
}

/**
 * Submit timesheets up to (and including) `maxDate`. The BE walks from
 * the user's last-submitted day forward and stamps `submitted` on every
 * day in the range. Returns 400 if any time entry on or before `maxDate`
 * is still `pending` — the user must approve every entry first.
 *
 * Invalidates: `timesheet-days` family (so the modal re-renders the new
 * statuses) AND `timesheet` family (so the calendar view's per-day data
 * stays fresh, even though the entries themselves didn't change).
 */
export function useSubmitTimesheets() {
  const queryClient = useQueryClient()

  return useMutation<SubmitTimesheetsResponse, Error, SubmitTimesheetsArgs>({
    mutationFn: async ({ maxDate }) => {
      const res = await apiClient<{ data: SubmitTimesheetsResponse }>('/api/timesheets', {
        method: 'POST',
        body: { maxDate },
      })
      return res.data
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: TIMESHEET_DAYS_FAMILY })
      queryClient.invalidateQueries({ queryKey: TIMESHEET_FAMILY })
    },
  })
}
